import { cloneElement, type CSSProperties, type ReactElement } from 'react';
import { XSmall } from '@doist/icons/brand';
import { AlarmClockFilled, AlarmClockOutline, DateDefault, DateMove, DateNone, DateToday, LabelFilled, LabelOutline, MoreFilled, Person, PriorityFlagFilled, PriorityFlagOutline, Sofa } from '@doist/icons/product-ui';
import { Text } from '@doist/marketist';
import cn from 'clsx';
import { type TFunction, useTranslation } from 'next-i18next';
import { ProductUIAvatar, type ProductUIAvatarProps } from '../product-ui-avatar/product-ui-avatar';
import { type ProductUIColors } from '../product-ui-types';
import { mapColorNameToCustomProp } from '../utils';
import css from './product-ui-task-attribute-pill.module.css';
export type VariantDueDates = 'dueDatePlaceHolder' | 'dueDateToday' | 'dueDateTomorrow' | 'dueDateNextWeek' | 'dueDateThisWeekend' | 'dueDateNoDate' | 'dueDateCustom';
export type VariantPriority = 'priorityPlaceholder' | 'priorityP1' | 'priorityP2' | 'priorityP3' | 'priorityP4';
type VariantLabel = 'labelPlaceholder' | 'labelCustom';
type VariantReminder = 'reminderPlaceholder' | 'reminderCustom';
export type VariantAssignee = 'assigneePlaceholder' | 'assigneeCustom';
type ProductUITaskAttributePillVariant = VariantDueDates | VariantPriority | VariantLabel | VariantReminder | VariantAssignee | 'overflow';
export type ProductUITaskAttributePillProps = {
  id?: string;
  suffixIcon?: ReactElement;
  variant: ProductUITaskAttributePillVariant;
  hovered?: boolean;
  children?: React.ReactNode;
  color?: ProductUIColors;
  avatar?: ProductUIAvatarProps['character'];
};
function getLabel(t: TFunction, variant: ProductUITaskAttributePillProps['variant']) {
  switch (variant) {
    case 'dueDatePlaceHolder':
      return t('taskAttributePill.dueDate');
    case 'dueDateToday':
      return t('taskAttributePill.today');
    case 'dueDateTomorrow':
      return t('taskAttributePill.tomorrow');
    case 'dueDateNextWeek':
      return t('taskAttributePill.nextWeek');
    case 'dueDateThisWeekend':
      return t('taskAttributePill.thisWeekend');
    case 'dueDateNoDate':
      return t('taskAttributePill.noDate');
    case 'priorityPlaceholder':
      return t('taskAttributePill.priority');
    case 'priorityP1':
      return t('taskAttributePill.p1');
    case 'priorityP2':
      return t('taskAttributePill.p2');
    case 'priorityP3':
      return t('taskAttributePill.p3');
    case 'priorityP4':
      return t('taskAttributePill.p4');
    case 'labelPlaceholder':
      return t('taskAttributePill.label');
    case 'reminderPlaceholder':
      return t('taskAttributePill.reminders');
    case 'assigneePlaceholder':
      return t('taskAttributePill.assignee');
    default:
      return null;
  }
}
function getPrefixIcon(variant: ProductUITaskAttributePillProps['variant']) {
  switch (variant) {
    case 'dueDatePlaceHolder':
    case 'dueDateTomorrow':
    case 'dueDateCustom':
      return DateDefault;
    case 'dueDateToday':
      return DateToday;
    case 'dueDateNextWeek':
      return DateMove;
    case 'dueDateThisWeekend':
      return Sofa;
    case 'dueDateNoDate':
      return DateNone;
    case 'priorityPlaceholder':
      return PriorityFlagOutline;
    case 'priorityP1':
    case 'priorityP2':
    case 'priorityP3':
    case 'priorityP4':
      return PriorityFlagFilled;
    case 'labelPlaceholder':
      return LabelOutline;
    case 'labelCustom':
      return LabelFilled;
    case 'reminderPlaceholder':
      return AlarmClockOutline;
    case 'reminderCustom':
      return AlarmClockFilled;
    case 'assigneePlaceholder':
      return Person;
    case 'overflow':
      return MoreFilled;
    default:
      return null;
  }
}
export function ProductUITaskAttributePill({
  id,
  variant,
  suffixIcon,
  hovered,
  children,
  color,
  avatar,
  ...rest
}: ProductUITaskAttributePillProps) {
  const {
    t
  } = useTranslation('productui');
  const variantLabel = getLabel(t, variant);
  const PrefixIcon = getPrefixIcon(variant);
  const showFilledIcon = !variant.toLowerCase().includes('placeholder') && variant !== 'overflow';
  const Icon = suffixIcon ? cloneElement(suffixIcon, {
    className: css.suffixIcon,
    width: 20,
    height: 20
  }) : null;
  const colorCustomProp = color ? mapColorNameToCustomProp(color, {
    grey: 'var(--display-onlight-secondary)'
  }) : undefined;
  const customProps = {
    '--pill-color': colorCustomProp,
    '--icon-color': colorCustomProp
  } as CSSProperties;
  return <Text tag="div" size="body-xs" weight="medium" id={id} className={cn(css.pill, css[variant], {
    [css.hovered]: hovered
  })} style={customProps} {...rest} data-sentry-element="Text" data-sentry-component="ProductUITaskAttributePill" data-sentry-source-file="product-ui-task-attribute-pill.tsx">
            {avatar ? <ProductUIAvatar size={16} character={avatar} /> : PrefixIcon ? <PrefixIcon className={css.prefixIcon} /> : null}
            {children || variantLabel ? <span className={css.content}>{children ? children : variantLabel}</span> : null}
            {Icon ? Icon : null}
            {showFilledIcon ? <XSmall className={css.fillIcon} /> : null}
        </Text>;
}